import { useMemo } from 'react';
import { Badge } from './Badge.jsx';
import { ScrollX } from './ScrollX.jsx';
import {
  DCMA_METRIC_LABELS_V6,
  DCMA_THRESHOLD_LABELS_V6,
  DCMA_PANEL_FOOTNOTE,
  getV6ResultDisplay,
} from '../../engine/dcmaLabelsV6.js';

function statusForPoint(point) {
  // Mirror the PDF (_dcma-table.ejs): only a genuinely PENDING stub is "not
  // assessed"; otherwise a real pass/fail. #12 Resource loading is a real
  // check (#36) — here it is a FAIL (0.0%), not N/A.
  if (point.status === 'pending') return 'NA';
  if (point.pass === true) return 'OK';
  if (point.pass === false) return 'CRITICAL';
  return 'NA';
}

function resultColorForStatus(status) {
  if (status === 'OK') return 'var(--pass)';
  if (status === 'CRITICAL') return 'var(--crit)';
  return 'var(--gray-600)';
}

/**
 * DCMA 14-Point table. Mirrors the PDF partial _dcma-table.ejs (shared label
 * source dcmaLabelsV6.js) so the two surfaces never diverge (Rule 12):
 *   - Header pass ratio "{passCount} of {denom} pass". denom excludes only
 *     genuinely PENDING points (p.status === 'pending') — never a hardcoded
 *     #12. Resource loading (#12) is a real check (#36); on the reference
 *     fixture it is 0.0% / FAIL, counted in the denominator → "7 of 14".
 *   - A 14-row table (#, Metric, Threshold, Result, Status) from
 *     analysis.dcma.points in engine order; result via shared getV6ResultDisplay.
 */
export function DcmaTable({ points }) {
  const sorted = useMemo(() => {
    const pts = Array.isArray(points) ? points : [];
    return [...pts].sort((a, b) => (a.dcmaNumber || 0) - (b.dcmaNumber || 0));
  }, [points]);

  const denom = sorted.filter((p) => p.status !== 'pending').length;
  const passCount = sorted.filter((p) => p.pass === true).length;
  const pct = denom > 0 ? Math.round((passCount / denom) * 100) : 0;
  const barClass = pct >= 80 ? 'bar-pass' : pct >= 55 ? 'bar-warn' : 'bar-crit';

  return (
    <section className="card dcma-panel-card" aria-label="DCMA 14-Point Compliance">
      <div className="dcma-panel-header">
        <div className="dcma-panel-title-block">
          <div className="dcma-panel-title">DCMA 14-Point Compliance</div>
          <div className="dcma-panel-sub">
            Industry-standard schedule quality assessment framework
          </div>
        </div>
        <div className="dcma-panel-meter">
          <div
            className="dcma-ratio-bar"
            role="img"
            aria-label={`${passCount} of ${denom} DCMA points pass`}
          >
            <div
              className={`dcma-ratio-fill ${barClass}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="dcma-ratio-label">
            {passCount} of {denom} pass
          </span>
        </div>
      </div>
      {/* MOBILE-FIT M2: the table slides at narrow widths; # + Metric stay
          sticky so a row keeps its identity while Threshold/Result/Status
          scroll into reach. Same columns, same order — nothing dropped. */}
      <ScrollX className="dcma-scroll">
      <table className="dcma-table dcma-table-sticky">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Metric</th>
            <th scope="col">Threshold</th>
            <th scope="col">Result</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => {
            const status = statusForPoint(p);
            const label = status === 'OK' ? 'PASS' : status === 'CRITICAL' ? 'FAIL' : 'N/A';
            const resultColor = resultColorForStatus(status);
            const metricLabel = DCMA_METRIC_LABELS_V6[p.dcmaNumber] || p.metric;
            const thresholdLabel = DCMA_THRESHOLD_LABELS_V6[p.dcmaNumber] || p.dcmaThreshold || '—';
            const resultText = getV6ResultDisplay(p);
            return (
              <tr key={p.dcmaNumber}>
                <td>{p.dcmaNumber}</td>
                <td>{metricLabel}</td>
                <td>{thresholdLabel}</td>
                <td style={{ color: resultColor, fontWeight: status === 'NA' ? 400 : 600 }}>
                  {resultText}
                </td>
                <td>
                  <Badge variant={status} size="sm">{label}</Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </ScrollX>
      <p className="dcma-panel-footnote">{DCMA_PANEL_FOOTNOTE}</p>
    </section>
  );
}
